import { wouldCreateCycle } from "~shared/utils/wouldCreateCycle";

/** Flat or nested tree node from project sidebar state */
export interface TreeNode {
  id: string;
  type: "document" | "folder";
  parentId?: string | null;
  displayOrder: number;
  children?: TreeNode[];
  name?: string;
  title?: string;
  collapsed?: boolean;
  toggleCollapsed?: () => void;
}

export interface SelectionResult {
  selectedIds: string[];
  anchorId: string | null;
}

export interface ValidationResult {
  valid: boolean;
  reason: string | null;
}

export interface MovePlan {
  id: string;
  parentId: string;
  displayOrder: number;
}

export interface FolderTreeItem {
  id: string;
  name: string;
  parentId?: string | null;
  displayOrder: number;
  collapsed?: boolean;
  [key: string]: unknown;
}

export interface DocumentTreeItem {
  id: string;
  type?: "document";
  parentId?: string | null;
  displayOrder: number;
  [key: string]: unknown;
}

/**
 * IDs in depth-first order as rendered. Skips children of collapsed folders: use `collapsedFolderIds`
 * for UI state (recommended), or `node.collapsed` on each folder when the third argument is omitted.
 */
export function buildVisibleNodeIds(tree: TreeNode[], result: string[] = [], collapsedFolderIds?: Set<string>): string[] {
  tree.forEach((node) => {
    result.push(node.id);
    const folderCollapsed =
      node.type === "folder" &&
      (collapsedFolderIds != null ? collapsedFolderIds.has(node.id) : Boolean(node.collapsed));
    if (folderCollapsed) {
      return;
    }
    if (node.children?.length) {
      buildVisibleNodeIds(node.children, result, collapsedFolderIds);
    }
  });

  return result;
}

/** Batch tree moves are allowed when dropping on folder body, end-of-folder slot, or list separators. */
export function isBatchFolderDropTarget(dropType: string | undefined): boolean {
  return dropType === "folder" || dropType === "separator" || dropType === "folder-end";
}

/** Drop ids that are not in the visible list (e.g. inside a newly collapsed folder). */
export function pruneSelectionToVisible({
  selectedIds,
  anchorId,
  visibleNodeIds,
}: {
  selectedIds: string[];
  anchorId: string | null;
  visibleNodeIds: string[];
}): SelectionResult {
  const visible = new Set(visibleNodeIds);
  const pruned = selectedIds.filter((id) => visible.has(id));
  let nextAnchor = anchorId;
  if (nextAnchor != null && !visible.has(nextAnchor)) {
    nextAnchor = null;
  }
  return { selectedIds: pruned, anchorId: nextAnchor };
}

export function getNextSelection({
  clickedId,
  visibleNodeIds,
  selectedIds,
  anchorId,
  isShiftKey,
  isMetaKey,
}: {
  clickedId: string;
  visibleNodeIds: string[];
  selectedIds: string[];
  anchorId: string | null;
  isShiftKey: boolean;
  isMetaKey: boolean;
}): SelectionResult {
  if (isShiftKey && anchorId && visibleNodeIds.includes(anchorId) && visibleNodeIds.includes(clickedId)) {
    const startIndex = visibleNodeIds.indexOf(anchorId);
    const endIndex = visibleNodeIds.indexOf(clickedId);
    return {
      selectedIds: visibleNodeIds.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1),
      anchorId,
    };
  }

  if (isMetaKey) {
    const alreadySelected = selectedIds.includes(clickedId);
    return {
      selectedIds: alreadySelected ? selectedIds.filter((id) => id !== clickedId) : [...selectedIds, clickedId],
      anchorId: clickedId,
    };
  }

  const isOnlySelectedItem = selectedIds.length === 1 && selectedIds[0] === clickedId;
  if (isOnlySelectedItem) {
    return {
      selectedIds: [],
      anchorId: null,
    };
  }

  return {
    selectedIds: [clickedId],
    anchorId: clickedId,
  };
}

function getParentId(nodeId: string, nodesById: Record<string, TreeNode | undefined>): string | undefined {
  return nodesById[nodeId]?.parentId ?? undefined;
}

function getAncestorIds(nodeId: string, nodesById: Record<string, TreeNode | undefined>): string[] {
  const ancestors: string[] = [];
  let currentId = getParentId(nodeId, nodesById);

  while (currentId && currentId !== "none") {
    ancestors.push(currentId);
    currentId = getParentId(currentId, nodesById);
  }

  return ancestors;
}

export function getTopLevelSelectedNodeIds(selectedIds: string[], nodesById: Record<string, TreeNode | undefined>): string[] {
  return selectedIds.filter((id) => {
    const ancestors = getAncestorIds(id, nodesById);
    return !ancestors.some((ancestorId) => selectedIds.includes(ancestorId));
  });
}

export function getBatchMoveItemCount(selectedIds: string[], nodesById: Record<string, TreeNode | undefined>): number {
  return getTopLevelSelectedNodeIds(selectedIds, nodesById).length;
}

function normalizedParentKey(node: TreeNode | undefined): string {
  const parentId = node?.parentId;
  return parentId && parentId !== "none" ? parentId : "none";
}

function childrenOfParent(nodesById: Record<string, TreeNode | undefined>, destinationParentId: string | null | undefined): TreeNode[] {
  const dest = destinationParentId && destinationParentId !== "none" ? destinationParentId : "none";
  return Object.values(nodesById).filter((node): node is TreeNode => Boolean(node) && normalizedParentKey(node) === dest);
}

export function validateBatchFolderDrop({
  selectedIds,
  destinationFolderId,
  nodesById,
}: {
  selectedIds: string[];
  destinationFolderId: string | null | undefined;
  nodesById: Record<string, TreeNode | undefined>;
}): ValidationResult {
  const topLevelSelectedIds = getTopLevelSelectedNodeIds(selectedIds, nodesById);
  // Descendant selections are OK: move plans use top-level ids only (`getTopLevelSelectedNodeIds`).

  if (destinationFolderId != null && topLevelSelectedIds.includes(destinationFolderId)) {
    return { valid: false, reason: "destination_is_selected" };
  }

  if (
    destinationFolderId &&
    destinationFolderId !== "none" &&
    topLevelSelectedIds.some(
      (id) =>
        nodesById[id]?.type === "folder" &&
        wouldCreateCycle((candidateId: string) => getParentId(candidateId, nodesById) ?? null, id, destinationFolderId)
    )
  ) {
    return { valid: false, reason: "destination_inside_selected_folder" };
  }

  return { valid: true, reason: null };
}

export function isBatchDropDisabled({
  selectedIds,
  destinationParentId,
  nestingLevel,
  nodesById,
}: {
  selectedIds: string[];
  destinationParentId: string | null | undefined;
  nestingLevel: number;
  nodesById: Record<string, TreeNode | undefined>;
}): boolean {
  const topLevelSelectedIds = getTopLevelSelectedNodeIds(selectedIds, nodesById);
  const hasSelectedFolder = topLevelSelectedIds.some((id) => nodesById[id]?.type === "folder");

  if (!hasSelectedFolder) {
    return false;
  }

  if (nestingLevel >= 19) {
    return true;
  }

  return !validateBatchFolderDrop({
    selectedIds: topLevelSelectedIds,
    destinationFolderId: destinationParentId,
    nodesById,
  }).valid;
}

export function buildBatchFolderMovePlan({
  selectedIds,
  visibleNodeIds,
  destinationFolderId,
  nodesById,
}: {
  selectedIds: string[];
  visibleNodeIds: string[];
  destinationFolderId: string | null | undefined;
  nodesById: Record<string, TreeNode | undefined>;
}): MovePlan[] {
  const selectedTopLevelIds = getTopLevelSelectedNodeIds(selectedIds, nodesById).sort(
    (leftId, rightId) => visibleNodeIds.indexOf(leftId) - visibleNodeIds.indexOf(rightId)
  );
  const destParent = destinationFolderId && destinationFolderId !== "none" ? destinationFolderId : "none";
  const destinationChildren = childrenOfParent(nodesById, destParent)
    .filter((node) => !selectedTopLevelIds.includes(node.id))
    .sort((leftNode, rightNode) => leftNode.displayOrder - rightNode.displayOrder);

  let nextDisplayOrder = destinationChildren.length ? Math.max(...destinationChildren.map((node) => node.displayOrder)) : 0;

  return selectedTopLevelIds.map((id) => {
    nextDisplayOrder += 1;
    return {
      id,
      parentId: destParent,
      displayOrder: nextDisplayOrder,
    };
  });
}

/**
 * Insert moved items at a separator position (between siblings). `insertBeforeDisplayOrder` is the
 * midpoint from the UI; items with displayOrder >= this value stay after the insertion.
 * Use `Number.POSITIVE_INFINITY` to append at the end (e.g. folder-end drop).
 */
export function buildBatchSeparatorMovePlan({
  selectedIds,
  visibleNodeIds,
  destinationParentId,
  insertBeforeDisplayOrder,
  nodesById,
}: {
  selectedIds: string[];
  visibleNodeIds: string[];
  destinationParentId: string | null | undefined;
  insertBeforeDisplayOrder: number;
  nodesById: Record<string, TreeNode | undefined>;
}): MovePlan[] {
  const selectedTopLevelIds = getTopLevelSelectedNodeIds(selectedIds, nodesById).sort(
    (leftId, rightId) => visibleNodeIds.indexOf(leftId) - visibleNodeIds.indexOf(rightId)
  );
  const movingSet = new Set(selectedTopLevelIds);
  const destParent = destinationParentId && destinationParentId !== "none" ? destinationParentId : "none";

  const existingChildren = childrenOfParent(nodesById, destParent)
    .filter((node) => !movingSet.has(node.id))
    .sort((leftNode, rightNode) => leftNode.displayOrder - rightNode.displayOrder);

  const insertBefore =
    insertBeforeDisplayOrder === Number.POSITIVE_INFINITY
      ? existingChildren.length
      : existingChildren.findIndex((node) => node.displayOrder >= insertBeforeDisplayOrder);

  const k = insertBefore === -1 ? existingChildren.length : insertBefore;

  const orderedIds = [
    ...existingChildren.slice(0, k).map((node) => node.id),
    ...selectedTopLevelIds,
    ...existingChildren.slice(k).map((node) => node.id),
  ];

  return orderedIds.map((id, index) => ({
    id,
    parentId: destParent,
    displayOrder: index + 1,
  }));
}

function buildTree(node: TreeNode, nodes: TreeNode[]): TreeNode {
  if (node.type === "document") {
    return node;
  }

  const children = nodes
    .filter((candidate) => candidate.parentId === node.id)
    .sort((leftNode, rightNode) => leftNode.displayOrder - rightNode.displayOrder);
  children.forEach((child) => buildTree(child, nodes));
  node.children = children;
  return node;
}

/** Apply batch moves and renumber displayOrder per parent (do not sort the flat node list globally). */
export function applyBatchTreeMoves(allNodes: TreeNode[], movePlan: MovePlan[]): TreeNode[] {
  const moveMap = new Map(movePlan.map((move) => [move.id, move]));

  const updated = allNodes.map((node) => {
    const move = moveMap.get(node.id);
    const nextParent = move ? move.parentId : node.parentId;
    const nextOrder = move ? move.displayOrder : node.displayOrder;
    const parentId = nextParent && nextParent !== "none" ? nextParent : "none";
    return {
      ...node,
      parentId,
      displayOrder: nextOrder,
      children: undefined,
    };
  });

  const byParent = new Map<string, TreeNode[]>();
  for (const node of updated) {
    const p = node.parentId && node.parentId !== "none" ? node.parentId : "none";
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p)!.push(node);
  }

  const renumbered: TreeNode[] = [];
  for (const [, siblings] of byParent) {
    siblings.sort((leftNode, rightNode) => leftNode.displayOrder - rightNode.displayOrder);
    siblings.forEach((node, index) => {
      const parentId = node.parentId && node.parentId !== "none" ? node.parentId : "none";
      renumbered.push({
        ...node,
        parentId,
        displayOrder: index + 1,
      });
    });
  }

  const rootNodes = renumbered.filter((node) => !node.parentId || node.parentId === "none");
  rootNodes.sort((leftNode, rightNode) => leftNode.displayOrder - rightNode.displayOrder);
  rootNodes.forEach((node) => buildTree(node, renumbered));
  return rootNodes;
}

function flattenTree(nodes: TreeNode[], result: TreeNode[] = []): TreeNode[] {
  for (const node of nodes) {
    result.push(node);
    if (node.children?.length) {
      flattenTree(node.children, result);
    }
  }
  return result;
}

/**
 * Build one atomic project payload for a batch tree move by applying the move plan locally,
 * then copying the final parentId/displayOrder back onto the persisted folder/document arrays.
 */
export function buildBatchProjectUpdate({
  folders,
  contents,
  movePlan,
}: {
  folders: FolderTreeItem[];
  contents: DocumentTreeItem[];
  movePlan: MovePlan[];
}) {
  const allNodes: TreeNode[] = [
    ...folders.map((folder) => ({
      id: folder.id,
      type: "folder" as const,
      name: folder.name,
      parentId: folder.parentId ?? "none",
      displayOrder: folder.displayOrder,
      collapsed: folder.collapsed,
    })),
    ...contents.map((document) => ({
      id: document.id,
      type: "document" as const,
      parentId: document.parentId ?? "none",
      displayOrder: document.displayOrder,
    })),
  ];

  const nextTree = applyBatchTreeMoves(allNodes, movePlan);
  const nextPositions = new Map(
    flattenTree(nextTree).map((node) => [
      node.id,
      {
        parentId: node.parentId && node.parentId !== "none" ? node.parentId : "none",
        displayOrder: node.displayOrder,
      },
    ])
  );

  return {
    folders: folders.map((folder) => {
      const next = nextPositions.get(folder.id);
      return next ? { ...folder, parentId: next.parentId, displayOrder: next.displayOrder } : folder;
    }),
    contents: contents.map((document) => {
      const next = nextPositions.get(document.id);
      return next ? { ...document, parentId: next.parentId, displayOrder: next.displayOrder } : document;
    }),
  };
}

/** @deprecated Use applyBatchTreeMoves */
export const applyBatchFolderMove = applyBatchTreeMoves;
